import { useState, FormEvent, ChangeEvent } from 'react';
import axios from 'axios';
import { FiSave, FiEye, FiRefreshCw } from 'react-icons/fi';

interface LevelFormData {
  levelNumber: number;
  name: string;
  description: string;
  gridSize: { width: number; height: number };
  gemTypes: number;
  moveLimit: number;
  timeLimit: number;
  goals: Array<{
    type: 'score' | 'destroy' | 'collect' | 'drop';
    targetType: string;
    targetCount: number;
  }>;
  obstacles: Array<{
    type: 'ice' | 'block' | 'locked' | 'empty';
    positions: Array<{ x: number; y: number }>;
  }>;
  availableBoosters: string[];
  parScore: number;
  goldScore: number;
  perfectScore: number;
}

const LevelGenerator = () => {
  const [formData, setFormData] = useState<LevelFormData>({
    levelNumber: 1,
    name: '',
    description: '',
    gridSize: { width: 8, height: 8 },
    gemTypes: 5,
    moveLimit: 20,
    timeLimit: 0,
    goals: [{ type: 'score', targetType: '', targetCount: 1000 }],
    obstacles: [],
    availableBoosters: [],
    parScore: 1000,
    goldScore: 2000,
    perfectScore: 3500
  });

  const [jsonPreview, setJsonPreview] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (field: keyof LevelFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGridSizeChange = (dimension: 'width' | 'height', value: number) => {
    setFormData(prev => ({
      ...prev,
      gridSize: { ...prev.gridSize, [dimension]: value }
    }));
  };

  const addGoal = () => {
    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, { type: 'score', targetType: '', targetCount: 1000 }]
    }));
  };

  const updateGoal = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map((goal, i) => 
        i === index ? { ...goal, [field]: value } : goal
      )
    }));
  };

  const removeGoal = (index: number) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const addObstacle = (type: 'ice' | 'block' | 'locked' | 'empty') => {
    setFormData(prev => ({
      ...prev,
      obstacles: [...prev.obstacles, { type, positions: [] }]
    }));
  };

  const addObstaclePosition = (obstacleIndex: number, x: number, y: number) => {
    setFormData(prev => ({
      ...prev,
      obstacles: prev.obstacles.map((obs, i) =>
        i === obstacleIndex
          ? { ...obs, positions: [...obs.positions, { x, y }] }
          : obs
      )
    }));
  };

  const removeObstaclePosition = (obstacleIndex: number, posIndex: number) => {
    setFormData(prev => ({
      ...prev,
      obstacles: prev.obstacles.map((obs, i) =>
        i === obstacleIndex
          ? { ...obs, positions: obs.positions.filter((_, j) => j !== posIndex) }
          : obs
      )
    }));
  };

  const toggleBooster = (booster: string) => {
    setFormData(prev => ({
      ...prev,
      availableBoosters: prev.availableBoosters.includes(booster)
        ? prev.availableBoosters.filter(b => b !== booster)
        : [...prev.availableBoosters, booster]
    }));
  };

  const generateJsonPreview = () => {
    setJsonPreview(JSON.stringify(formData, null, 2));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/levels', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Level created successfully!' });
      
      // Reset form
      setFormData({
        levelNumber: formData.levelNumber + 1,
        name: '',
        description: '',
        gridSize: { width: 8, height: 8 },
        gemTypes: 5,
        moveLimit: 20,
        timeLimit: 0,
        goals: [{ type: 'score', targetType: '', targetCount: 1000 }],
        obstacles: [],
        availableBoosters: [],
        parScore: 1000,
        goldScore: 2000,
        perfectScore: 3500
      });
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to create level' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const boosterOptions = ['shuffle', 'bomb', 'rocket', 'color_bomb', 'hammer'];

  return (
    <div className="level-generator">
      <h2>Level Generator</h2>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="generator-form">
        {/* Basic Info */}
        <section className="form-section card">
          <h3>Basic Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Level Number</label>
              <input
                type="number"
                className="input"
                value={formData.levelNumber}
                onChange={(e) => handleInputChange('levelNumber', parseInt(e.target.value))}
                min="1"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Level Name</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter level name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              className="input"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Level description"
              rows={2}
            />
          </div>
        </section>

        {/* Grid Settings */}
        <section className="form-section card">
          <h3>Grid Settings</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Grid Width</label>
              <input
                type="number"
                className="input"
                value={formData.gridSize.width}
                onChange={(e) => handleGridSizeChange('width', parseInt(e.target.value))}
                min="5"
                max="12"
              />
            </div>
            
            <div className="form-group">
              <label>Grid Height</label>
              <input
                type="number"
                className="input"
                value={formData.gridSize.height}
                onChange={(e) => handleGridSizeChange('height', parseInt(e.target.value))}
                min="5"
                max="12"
              />
            </div>
            
            <div className="form-group">
              <label>Gem Types (4-7)</label>
              <input
                type="number"
                className="input"
                value={formData.gemTypes}
                onChange={(e) => handleInputChange('gemTypes', parseInt(e.target.value))}
                min="4"
                max="7"
              />
            </div>
          </div>
        </section>

        {/* Limits */}
        <section className="form-section card">
          <h3>Limits</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Move Limit (0 for unlimited)</label>
              <input
                type="number"
                className="input"
                value={formData.moveLimit}
                onChange={(e) => handleInputChange('moveLimit', parseInt(e.target.value))}
                min="0"
                max="100"
              />
            </div>
            
            <div className="form-group">
              <label>Time Limit (0 for unlimited)</label>
              <input
                type="number"
                className="input"
                value={formData.timeLimit}
                onChange={(e) => handleInputChange('timeLimit', parseInt(e.target.value))}
                min="0"
                max="600"
              />
            </div>
          </div>
        </section>

        {/* Goals */}
        <section className="form-section card">
          <div className="section-header">
            <h3>Goals</h3>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addGoal}>
              Add Goal
            </button>
          </div>
          
          {formData.goals.map((goal, index) => (
            <div key={index} className="goal-editor">
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    className="input"
                    value={goal.type}
                    onChange={(e) => updateGoal(index, 'type', e.target.value)}
                  >
                    <option value="score">Score</option>
                    <option value="destroy">Destroy</option>
                    <option value="collect">Collect</option>
                    <option value="drop">Drop</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Target Type (optional)</label>
                  <input
                    type="text"
                    className="input"
                    value={goal.targetType}
                    onChange={(e) => updateGoal(index, 'targetType', e.target.value)}
                    placeholder="e.g., red, blue"
                  />
                </div>
                
                <div className="form-group">
                  <label>Count</label>
                  <input
                    type="number"
                    className="input"
                    value={goal.targetCount}
                    onChange={(e) => updateGoal(index, 'targetCount', parseInt(e.target.value))}
                    min="1"
                  />
                </div>
                
                <button
                  type="button"
                  className="btn-icon danger"
                  onClick={() => removeGoal(index)}
                  disabled={formData.goals.length === 1}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </section>

        {/* Score Thresholds */}
        <section className="form-section card">
          <h3>Score Thresholds</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>1 Star (Par)</label>
              <input
                type="number"
                className="input"
                value={formData.parScore}
                onChange={(e) => handleInputChange('parScore', parseInt(e.target.value))}
                min="100"
              />
            </div>
            
            <div className="form-group">
              <label>2 Stars (Gold)</label>
              <input
                type="number"
                className="input"
                value={formData.goldScore}
                onChange={(e) => handleInputChange('goldScore', parseInt(e.target.value))}
                min="100"
              />
            </div>
            
            <div className="form-group">
              <label>3 Stars (Perfect)</label>
              <input
                type="number"
                className="input"
                value={formData.perfectScore}
                onChange={(e) => handleInputChange('perfectScore', parseInt(e.target.value))}
                min="100"
              />
            </div>
          </div>
        </section>

        {/* Boosters */}
        <section className="form-section card">
          <h3>Available Boosters</h3>
          
          <div className="booster-grid">
            {boosterOptions.map(booster => (
              <label key={booster} className={`booster-checkbox ${formData.availableBoosters.includes(booster) ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={formData.availableBoosters.includes(booster)}
                  onChange={() => toggleBooster(booster)}
                />
                <span>{booster.replace('_', ' ').toUpperCase()}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={generateJsonPreview}>
            <FiEye /> Preview JSON
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            <FiSave /> {isSaving ? 'Saving...' : 'Save Level'}
          </button>
        </div>
      </form>

      {/* JSON Preview Modal */}
      {jsonPreview && (
        <div className="json-modal" onClick={() => setJsonPreview('')}>
          <div className="json-modal-content card" onClick={(e) => e.stopPropagation()}>
            <div className="json-modal-header">
              <h3>Level JSON Preview</h3>
              <button className="btn-icon" onClick={() => setJsonPreview('')}>✕</button>
            </div>
            <pre className="json-code">{jsonPreview}</pre>
            <div className="json-modal-actions">
              <button className="btn btn-secondary" onClick={() => setJsonPreview('')}>
                Close
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  navigator.clipboard.writeText(jsonPreview);
                  setMessage({ type: 'success', text: 'JSON copied to clipboard!' });
                }}
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .level-generator {
          max-width: 900px;
        }
        .level-generator h2 {
          margin-bottom: var(--spacing-lg);
        }
        .message {
          padding: var(--spacing-md);
          border-radius: var(--radius-md);
          margin-bottom: var(--spacing-lg);
        }
        .message.success {
          background: rgba(8, 217, 214, 0.1);
          border: 1px solid var(--neon-green);
          color: var(--neon-green);
        }
        .message.error {
          background: rgba(255, 46, 99, 0.1);
          border: 1px solid var(--neon-pink);
          color: var(--neon-pink);
        }
        .generator-form {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-lg);
        }
        .form-section {
          padding: var(--spacing-lg);
        }
        .form-section h3 {
          margin-bottom: var(--spacing-md);
          font-size: 1.125rem;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-md);
        }
        .section-header h3 {
          margin-bottom: 0;
        }
        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: var(--spacing-md);
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-sm);
        }
        .form-group label {
          font-size: 0.875rem;
          color: var(--text-secondary);
          font-weight: 500;
        }
        .btn-sm {
          padding: var(--spacing-xs) var(--spacing-md);
          font-size: 0.875rem;
        }
        .goal-editor {
          padding: var(--spacing-md);
          background: var(--bg-tertiary);
          border-radius: var(--radius-md);
          margin-bottom: var(--spacing-md);
        }
        .booster-grid {
          display: flex;
          flex-wrap: wrap;
          gap: var(--spacing-md);
        }
        .booster-checkbox {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          background: var(--bg-tertiary);
          border: 2px solid var(--border-color);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        .booster-checkbox.active {
          border-color: var(--neon-blue);
          background: rgba(0, 212, 255, 0.1);
        }
        .booster-checkbox input {
          display: none;
        }
        .form-actions {
          display: flex;
          gap: var(--spacing-md);
          justify-content: flex-end;
        }
        .json-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: var(--spacing-lg);
        }
        .json-modal-content {
          max-width: 800px;
          width: 100%;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
        }
        .json-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }
        .json-code {
          flex: 1;
          overflow: auto;
          padding: var(--spacing-lg);
          background: var(--bg-primary);
          font-family: 'Courier New', monospace;
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .json-modal-actions {
          display: flex;
          gap: var(--spacing-md);
          padding: var(--spacing-lg);
          border-top: 1px solid var(--border-color);
        }
        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default LevelGenerator;
